import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { getRoleLevel, ROLE_LEVEL } from "@shared/roles";
import { ThemeProvider } from "@/contexts/theme-context";
import { DevisCartProvider } from "@/contexts/DevisCartContext";
import { Component, type ErrorInfo, type ReactNode } from "react";

// ── ErrorBoundary global ─────────────────────────────────────────────────────

interface EBState { hasError: boolean; error: Error | null }

class ErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): EBState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-8">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-7 h-7 text-destructive" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground mb-2">Une erreur est survenue</h1>
              <p className="text-sm text-muted-foreground">
                Un problème inattendu a interrompu l'interface. Vos données sont en sécurité.
              </p>
              {this.state.error && (
                <pre className="mt-3 text-left text-xs bg-secondary rounded-lg p-3 overflow-auto text-muted-foreground max-h-32">
                  {this.state.error.message}
                </pre>
              )}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Recharger la page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

import LoginPage from "@/pages/login";
import Dashboard from "@/pages/dashboard";
import RobotList from "@/pages/robots/list";
import MissionList from "@/pages/missions/list";
import ClientList from "@/pages/clients/list";
import ClientDetail from "@/pages/clients/detail";
import VehicleList from "@/pages/vehicles/list";
import ProductList from "@/pages/products/list";
import ResellerList from "@/pages/resellers/list";
import ResellerDetail from "@/pages/resellers/detail";
import ProspectList from "@/pages/prospects/list";
import TaskList from "@/pages/tasks/list";
import TicketList from "@/pages/tickets/list";
import ChatsInbox from "@/pages/chats/inbox";
import SavList from "@/pages/sav/list";
import DevisList from "@/pages/devis/list";
import DevisEdit from "@/pages/devis/edit";
import BonLivraisonDetail from "@/pages/bons-livraison/detail";
import BonsLivraisonList from "@/pages/bons-livraison/list";
import CommandesList from "@/pages/commandes/list";
import CommandesDetail from "@/pages/commandes/detail";
import AchatsList from "@/pages/achats/list";
import AchatsDetail from "@/pages/achats/detail";
import StockList from "@/pages/stock/list";
import PartenairesList from "@/pages/partenaires/list";
import ParametresList from "@/pages/parametres/list";
import ParametresAgences from "@/pages/parametres/agences";
import EmailsPage from "@/pages/parametres/emails";
import RappelsPage from "@/pages/rappels/list";
import ParametresRappels from "@/pages/parametres/rappels";
import TypesMissionPage from "@/pages/parametres/types-mission";
import PlanningSemainePage from "@/pages/planning/semaine";
import SousTraitanceList from "@/pages/sous-traitance/list";
import SousTraitanceDetail from "@/pages/sous-traitance/detail";
import ContactList from "@/pages/contacts/list";
import ContactDetail from "@/pages/contacts/detail";
import ContactDuplicates from "@/pages/contacts/duplicates";
import ContactFusion from "@/pages/contacts/fusion";
import ScanAnalyticsPage from "@/pages/admin/scan-analytics";
import AdminFeedbackPage from "@/pages/admin/feedback";
import ResetPasswordPage from "@/pages/reset-password";
import AuthCallbackPage from "@/pages/auth-callback";
import NotFound from "@/pages/not-found";
import InstallPage from "@/pages/install";
import PublicDeclarationPage from "@/pages/declaration/PublicDeclarationPage";
import ProceduresPage from "@/pages/procedures/ProceduresPage";
import ActivitePage from "@/pages/activite";
import CandidatsPage from "@/pages/rh/candidats";
import CampagnesPage from "@/pages/rh/campagnes";
import { LayoutShell } from "@/components/layout-shell";
import { PushNotificationsManager } from "@/components/PushNotificationsManager";
import { useAppBadge } from "@/hooks/useAppBadge";

function AppBadge() {
  useAppBadge('/api/chats/unread-count');
  return null;
}

// minLevel : niveau ROLE_LEVEL minimum (technicien=1, manager=2, admin=3, super_admin=4)
// L'accès est piloté par profile.accessRole (jamais par profile.role qui est le champ métier).
function ProtectedRoute({ component: Component, minLevel = 1 }: { component: React.ComponentType, minLevel?: number }) {
  const { isAuthenticated, isLoading, profile } = useAuth();

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const dest = window.location.pathname + window.location.search;
    if (dest !== "/login" && dest !== "/" && !dest.startsWith("//")) {
      try {
        sessionStorage.setItem("redirectAfterLogin", dest);
      } catch (err) {
        console.error("[ProtectedRoute] sessionStorage write failed:", err);
      }
    }
    return <Redirect to="/login" />;
  }

  if (minLevel > 1 && profile && getRoleLevel(profile.accessRole) < minLevel) {
    return <Redirect to="/" />;
  }

  return (
    <LayoutShell>
      <Component />
    </LayoutShell>
  );
}

function Router() {
  return (
    <Switch>
      <Route path="/login" component={LoginPage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/auth/callback" component={AuthCallbackPage} />

      <Route path="/">
        <ProtectedRoute component={Dashboard} />
      </Route>

      <Route path="/robots">
        <ProtectedRoute component={RobotList} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/missions">
        <ProtectedRoute component={MissionList} />
      </Route>

      <Route path="/planning">
        <ProtectedRoute component={PlanningSemainePage} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/prospects">
        <ProtectedRoute component={ProspectList} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/clients">
        <ProtectedRoute component={ClientList} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/clients/:id">
        <ProtectedRoute component={ClientDetail} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/vehicles">
        <ProtectedRoute component={VehicleList} minLevel={ROLE_LEVEL.manager} />
      </Route>

      <Route path="/products">
        <ProtectedRoute component={ProductList} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/resellers">
        <ProtectedRoute component={ResellerList} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/resellers/:id">
        <ProtectedRoute component={ResellerDetail} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      {/* Redirections de compatibilité */}
      <Route path="/equipe">
        <Redirect to="/parametres?onglet=equipe" />
      </Route>
      <Route path="/technicians">
        <Redirect to="/parametres?onglet=equipe" />
      </Route>
      <Route path="/commercials">
        <Redirect to="/parametres?onglet=equipe" />
      </Route>
      <Route path="/administration">
        <Redirect to="/parametres?onglet=equipe" />
      </Route>
      <Route path="/integrations">
        <Redirect to="/parametres?onglet=integrations" />
      </Route>

      <Route path="/tasks">
        <ProtectedRoute component={TaskList} />
      </Route>

      <Route path="/tickets">
        <ProtectedRoute component={TicketList} />
      </Route>

      <Route path="/chats">
        <ProtectedRoute component={ChatsInbox} />
      </Route>
      <Route path="/chats/:id">
        <ProtectedRoute component={ChatsInbox} />
      </Route>

      <Route path="/sav">
        <ProtectedRoute component={SavList} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/devis">
        <ProtectedRoute component={DevisList} minLevel={ROLE_LEVEL.technicien} />
      </Route>
      <Route path="/devis/nouveau">
        <ProtectedRoute component={DevisEdit} minLevel={ROLE_LEVEL.technicien} />
      </Route>
      <Route path="/devis/:id/editer">
        <ProtectedRoute component={DevisEdit} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/preparation">
        <ProtectedRoute component={BonsLivraisonList} minLevel={ROLE_LEVEL.technicien} />
      </Route>
      <Route path="/bons-livraison/:id">
        <ProtectedRoute component={BonLivraisonDetail} minLevel={ROLE_LEVEL.technicien} />
      </Route>

      <Route path="/commandes">
        <ProtectedRoute component={CommandesList} minLevel={ROLE_LEVEL.manager} />
      </Route>
      <Route path="/commandes/:id">
        <ProtectedRoute component={CommandesDetail} minLevel={ROLE_LEVEL.manager} />
      </Route>

      <Route path="/achats">
        <ProtectedRoute component={AchatsList} minLevel={ROLE_LEVEL.manager} />
      </Route>
      <Route path="/achats/:id">
        <ProtectedRoute component={AchatsDetail} minLevel={ROLE_LEVEL.manager} />
      </Route>

      <Route path="/stock">
        <ProtectedRoute component={StockList} minLevel={ROLE_LEVEL.manager} />
      </Route>

      <Route path="/partenaires">
        <ProtectedRoute component={PartenairesList} minLevel={ROLE_LEVEL.super_admin} />
      </Route>

      <Route path="/parametres/partenaires">
        <ProtectedRoute component={PartenairesList} minLevel={ROLE_LEVEL.super_admin} />
      </Route>

      <Route path="/parametres/emails">
        <ProtectedRoute component={EmailsPage} minLevel={ROLE_LEVEL.super_admin} />
      </Route>

      <Route path="/parametres/rappels">
        <ProtectedRoute component={ParametresRappels} minLevel={ROLE_LEVEL.super_admin} />
      </Route>

      <Route path="/parametres/types-mission">
        <ProtectedRoute component={TypesMissionPage} minLevel={ROLE_LEVEL.super_admin} />
      </Route>

      <Route path="/parametres">
        <ProtectedRoute component={ParametresList} minLevel={ROLE_LEVEL.super_admin} />
      </Route>
      <Route path="/parametres/agences">
        <ProtectedRoute component={ParametresAgences} minLevel={ROLE_LEVEL.admin} />
      </Route>

      <Route path="/rappels">
        <ProtectedRoute component={RappelsPage} />
      </Route>

      <Route path="/sous-traitance">
        <ProtectedRoute component={SousTraitanceList} minLevel={ROLE_LEVEL.manager} />
      </Route>
      <Route path="/sous-traitance/:id">
        <ProtectedRoute component={SousTraitanceDetail} minLevel={ROLE_LEVEL.manager} />
      </Route>

      <Route path="/contacts/doublons">
        <ProtectedRoute component={ContactDuplicates} />
      </Route>
      <Route path="/contacts/fusion">
        <ProtectedRoute component={ContactFusion} />
      </Route>
      {/* Redirects de compatibilité — anciens liens (notifs, activity_feed) */}
      <Route path="/contacts/admin/fusions">
        <Redirect to="/contacts?onglet=fusions" />
      </Route>
      <Route path="/contacts/admin/a-completer">
        <Redirect to="/contacts?onglet=a-completer" />
      </Route>
      <Route path="/contacts/:id">
        <ProtectedRoute component={ContactDetail} />
      </Route>
      <Route path="/contacts">
        <ProtectedRoute component={ContactList} />
      </Route>

      <Route path="/admin/scan-analytics">
        <ProtectedRoute component={ScanAnalyticsPage} minLevel={ROLE_LEVEL.super_admin} />
      </Route>

      <Route path="/admin/feedback">
        <ProtectedRoute component={AdminFeedbackPage} minLevel={ROLE_LEVEL.super_admin} />
      </Route>

      <Route path="/procedures">
        <ProtectedRoute component={ProceduresPage} />
      </Route>

      <Route path="/activite">
        <ProtectedRoute component={ActivitePage} />
      </Route>

      <Route path="/rh/candidats">
        <ProtectedRoute component={CandidatsPage} minLevel={ROLE_LEVEL.manager} />
      </Route>

      <Route path="/rh/campagnes">
        <Redirect to="/rh/candidats" />
      </Route>

      <Route path="/install" component={InstallPage} />
      <Route path="/declaration/:token" component={PublicDeclarationPage} />

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <DevisCartProvider>
          <TooltipProvider>
            <ErrorBoundary>
              <Router />
              <PushNotificationsManager />
              <AppBadge />
              <Toaster />
            </ErrorBoundary>
          </TooltipProvider>
        </DevisCartProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
